import { Progress as RadixProgress } from "radix-ui";
import { twMerge } from "tailwind-merge";

interface BaseProgressProps {
  value: number;
  max?: number;
  className?: string;
  indicatorClassName?: string;
  showValue?: boolean;
  "aria-label"?: string;
  "aria-valuetext"?: string;
}

interface LinearProgressProps extends BaseProgressProps {
  variant?: "linear";
  startLabel?: string;
  endLabel?: string;
}

interface CircularProgressProps extends BaseProgressProps {
  variant: "circular";
  size?: number;
  strokeWidth?: number;
}

export type ProgressProps = LinearProgressProps | CircularProgressProps;

export function Progress(props: ProgressProps) {
  const {
    value,
    max = 100,
    className,
    indicatorClassName,
    showValue = false,
    variant = "linear",
    "aria-label": ariaLabel,
    "aria-valuetext": ariaValueText,
  } = props;

  const clampedValue = Math.min(Math.max(value, 0), max);
  const percentage = (clampedValue / max) * 100;

  if (variant === "circular") {
    const { size = 64, strokeWidth = 4 } = props as CircularProgressProps;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div
        className={twMerge(
          "relative inline-flex items-center justify-center",
          className,
        )}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={ariaLabel}
        aria-valuetext={ariaValueText}
      >
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={twMerge(
              "text-blue-400 transition-[stroke-dashoffset] duration-300 ease-out",
              indicatorClassName,
            )}
          />
        </svg>
        {showValue && (
          <span
            className={twMerge(
              "absolute text-sm font-bold text-blue-400",
              indicatorClassName,
            )}
          >
            {Math.round(percentage)}%
          </span>
        )}
      </div>
    );
  }

  const { startLabel, endLabel } = props as LinearProgressProps;

  const hasLabels = startLabel || endLabel;

  return (
    <div className="flex w-full items-center gap-4">
      <div className="flex w-full flex-col gap-1">
        {hasLabels && (
          <div className="flex items-center justify-between gap-2">
            {startLabel && (
              <span className="text-sm font-medium">{startLabel}</span>
            )}
            {endLabel && (
              <span className="text-textSubtle text-sm">{endLabel}</span>
            )}
          </div>
        )}
        <RadixProgress.Root
          value={clampedValue}
          max={max}
          aria-label={ariaLabel}
          getValueLabel={
            ariaValueText === undefined ? undefined : () => ariaValueText
          }
          className={twMerge(
            "bg-fill-neutral relative h-2 w-full overflow-hidden rounded-full",
            className,
          )}
        >
          <RadixProgress.Indicator
            className={twMerge(
              "h-full rounded-full bg-blue-400 transition-transform duration-300 ease-out",
              indicatorClassName,
            )}
            style={{ transform: `translateX(-${100 - percentage}%)` }}
          />
        </RadixProgress.Root>
      </div>
      {showValue && (
        <span className="min-w-[3rem] text-right text-sm font-bold text-blue-400">
          {Math.round(percentage)}%
        </span>
      )}
    </div>
  );
}
